import { Element, ElementContent, Properties, Root } from 'hast'
import rehypeParse from 'rehype-parse'
import rehypeSanitize from 'rehype-sanitize'
import rehypeStringify from 'rehype-stringify'
import { unified } from 'unified'
import { visit } from 'unist-util-visit'

import { convertFromMsoCellHtmlToHtml, unifiedRemoveMsoCellMarkup } from './markdown'
import { remarkStripHtmlComments } from './rehype-plugins'
import { allowedPastedEditorHtmlSchema } from './schema'

const SPREADSHEET_HTML_PATTERN =
  /(?:google-sheets-html-origin|data-sheets-(?:root|formula)|urn:schemas-microsoft-com:office:excel|mso-(?:data-placement|number-format|style)|class=["'][^"']*\bxl\d+)/i
const NOTION_HTML_PATTERN =
  /data-(?:block-id|content-editable-leaf|notion|page-id)|class=["'][^"']*\bnotion-/i
const SLACK_HTML_PATTERN = /data-stringify-(?:type|link|url)|\bp-rich_text_section\b/i
const BLOCK_TAG_NAMES = new Set([
  'blockquote',
  'div',
  'h1',
  'h2',
  'h3',
  'hr',
  'ol',
  'p',
  'pre',
  'table',
  'ul',
])

const SLACK_TAG_NAMES: Record<string, string> = {
  bold: 'strong',
  code: 'code',
  'code-block': 'pre',
  italic: 'em',
  link: 'a',
  'list-item': 'li',
  'ordered-list': 'ol',
  'ordered-list-item': 'li',
  paragraph: 'p',
  pre: 'pre',
  preformatted: 'pre',
  quote: 'blockquote',
  strike: 'del',
  'unordered-list': 'ul',
  'unordered-list-item': 'li',
}

export function convertFromExternalHtmlToHtml(html: string) {
  if (!html.trim()) {
    return ''
  }

  const convertedMsoCellHtml = convertFromMsoCellHtmlToHtml(html)
  if (convertedMsoCellHtml !== html) {
    return convertedMsoCellHtml
  }

  const isSpreadsheetHtml = SPREADSHEET_HTML_PATTERN.test(html)
  if (isSpreadsheetHtml && !/<table[\s>]/i.test(html)) {
    return convertSpreadsheetCellHtmlToParagraphs(html)
  }

  const isNotionHtml = NOTION_HTML_PATTERN.test(html)
  if (!isSpreadsheetHtml && !isNotionHtml && !SLACK_HTML_PATTERN.test(html)) {
    return html
  }

  return sanitizeExternalEditorHtml(html, isNotionHtml)
}

export function convertFromTabSeparatedTextToTableHtml(text: string) {
  if (!text.includes('\t')) {
    return undefined
  }

  const rows = parseTabSeparatedText(text)
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
  if (columnCount < 2 || (rows.length > 1 && rows.some(row => row.length !== columnCount))) {
    return undefined
  }

  const body = rows
    .map(row => `<tr>${row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
    .join('')
  return `<table><tbody>${body}</tbody></table>`
}

function sanitizeExternalEditorHtml(html: string, isNotionHtml: boolean) {
  return unified()
    .use(rehypeParse, { fragment: true })
    .use(remarkStripHtmlComments)
    .use(() => (tree: Root) => normalizeExternalEditorTree(tree, isNotionHtml))
    .use(rehypeSanitize, allowedPastedEditorHtmlSchema)
    .use(rehypeStringify)
    .processSync(html)
    .toString()
}

function normalizeExternalEditorTree(tree: Root, isNotionHtml: boolean) {
  visit(tree, 'element', (node: Element) => {
    normalizeSlackElement(node)
    if (isNotionHtml) {
      normalizeNotionElement(node)
    }
    wrapPreformattedContent(node)
  })

  visit(tree, 'element', (node: Element) => {
    if (node.tagName !== 'ul' && node.tagName !== 'ol') {
      return
    }

    node.children.forEach(child => {
      if (child.type === 'element' && child.tagName === 'div') {
        child.tagName = 'li'
      }
    })
  })
}

function normalizeSlackElement(node: Element) {
  const stringifyType = getStringProperty(node.properties, 'dataStringifyType')
  const slackTagName = stringifyType ? SLACK_TAG_NAMES[stringifyType] : undefined
  if (slackTagName) {
    node.tagName = slackTagName
  } else if (hasClassName(node, 'p-rich_text_section')) {
    node.tagName = 'p'
  }

  if (stringifyType === 'paragraph-break') {
    node.tagName = 'br'
    node.children = []
  }

  if (node.tagName !== 'a') {
    return
  }

  const href =
    getStringProperty(node.properties, 'href') ??
    getStringProperty(node.properties, 'dataStringifyLink') ??
    getStringProperty(node.properties, 'dataStringifyUrl')
  node.properties = href ? { href } : {}
}

function normalizeNotionElement(node: Element) {
  if (
    node.tagName === 'div' &&
    !node.children.some(child => child.type === 'element' && BLOCK_TAG_NAMES.has(child.tagName))
  ) {
    node.tagName = 'p'
  }
}

function wrapPreformattedContent(node: Element) {
  if (node.tagName !== 'pre') {
    return
  }

  if (node.children.length === 1 && node.children[0].type === 'element') {
    if (node.children[0].tagName === 'code') {
      return
    }
  }

  node.children = [createElement('code', node.children)]
}

function createElement(tagName: string, children: ElementContent[]): Element {
  return {
    type: 'element',
    tagName,
    properties: {},
    children,
  }
}

function getStringProperty(properties: Properties, propertyName: string) {
  const value = properties[propertyName]
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }
  return undefined
}

function hasClassName(node: Element, className: string) {
  const value = node.properties['className']
  return Array.isArray(value) && value.includes(className)
}

function convertSpreadsheetCellHtmlToParagraphs(html: string) {
  const cellHtml = unifiedRemoveMsoCellMarkup(html)
  return cellHtml
    .split(/<br\s*\/?>/gi)
    .map(text => `<p>${text}</p>`)
    .join('')
}

function parseTabSeparatedText(text: string) {
  const normalizedText = text.replace(/\r\n?/g, '\n')
  const rows: string[][] = [[]]
  let cell = ''
  let quoted = false

  for (let index = 0; index < normalizedText.length; index += 1) {
    const character = normalizedText[index]
    if (character === '"') {
      if (quoted && normalizedText[index + 1] === '"') {
        cell += '"'
        index += 1
      } else if (!quoted && cell === '') {
        quoted = true
      } else if (
        quoted &&
        (normalizedText[index + 1] === '\t' ||
          normalizedText[index + 1] === '\n' ||
          normalizedText[index + 1] === undefined)
      ) {
        quoted = false
      } else {
        cell += '"'
      }
      continue
    }

    if (!quoted && character === '\t') {
      rows[rows.length - 1].push(cell)
      cell = ''
      continue
    }

    if (!quoted && character === '\n') {
      rows[rows.length - 1].push(cell)
      rows.push([])
      cell = ''
      continue
    }

    cell += character
  }

  rows[rows.length - 1].push(cell)
  if (rows.length > 1 && rows[rows.length - 1].every(value => value === '')) {
    rows.pop()
  }
  return rows
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\n/g, '<br>')
}
